import React from "react";
import { ShieldCheck, Building, Sparkles, Calculator } from "lucide-react";

export default function MortgageBreakdownCard({
  amount = 0,
  principalAmount = 0,
  interestAmount = 0,
  escrowAmount = 0,
  deductibleAmount = null,
  deductiblePct = 100,
  readOnly = false,
  onChange,
}) {
  const total = parseFloat(amount) || 0;
  const principal = parseFloat(principalAmount) || 0;
  const interest = parseFloat(interestAmount) || 0;
  const escrow = parseFloat(escrowAmount) || 0;

  // Calculate deductible
  let calcDeductible = deductibleAmount != null && deductibleAmount !== ""
    ? parseFloat(deductibleAmount)
    : (interest + escrow > 0 ? (interest + escrow) : Math.max(0, total - principal));

  if (isNaN(calcDeductible)) calcDeductible = 0;

  const pct = total > 0 ? Math.min(100, Math.max(0, (calcDeductible / total) * 100)) : 100;
  const nonDeductible = Math.max(0, total - calcDeductible);

  const handlePrincipalChange = (val) => {
    if (!onChange) return;
    const p = parseFloat(val) || 0;
    const newDed = Math.max(0, total - p);
    const newPct = total > 0 ? Number(((newDed / total) * 100).toFixed(1)) : 100;
    const reason = `Mortgage Principal ($${p.toFixed(2)}) is non-deductible equity; $${newDed.toFixed(2)} (Interest + Escrow) is Schedule E deductible.`;
    onChange({
      principal_amount: p,
      deductible_amount: newDed.toFixed(2),
      deductible_pct: newPct,
      deductibility_reason: reason,
    });
  };

  const handleInterestChange = (val) => {
    if (!onChange) return;
    const i = parseFloat(val) || 0;
    const newDed = i + escrow;
    const newP = Math.max(0, total - newDed);
    const newPct = total > 0 ? Number(((newDed / total) * 100).toFixed(1)) : 100;
    const reason = `Mortgage Principal ($${newP.toFixed(2)}) is non-deductible equity; $${newDed.toFixed(2)} (Interest + Escrow) is Schedule E deductible.`;
    onChange({
      interest_amount: i,
      principal_amount: newP,
      deductible_amount: newDed.toFixed(2),
      deductible_pct: newPct,
      deductibility_reason: reason,
    });
  };

  const handleEscrowChange = (val) => {
    if (!onChange) return;
    const esc = parseFloat(val) || 0;
    const newDed = interest + esc;
    const newP = Math.max(0, total - newDed);
    const newPct = total > 0 ? Number(((newDed / total) * 100).toFixed(1)) : 100;
    const reason = `Mortgage Principal ($${newP.toFixed(2)}) is non-deductible equity; $${newDed.toFixed(2)} (Interest + Escrow) is Schedule E deductible.`;
    onChange({
      escrow_amount: esc,
      principal_amount: newP,
      deductible_amount: newDed.toFixed(2),
      deductible_pct: newPct,
      deductibility_reason: reason,
    });
  };

  return (
    <div className="rounded-2xl border border-emerald-500/20 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-950/15 p-4 sm:p-5 space-y-4">
      {/* Header Banner */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
            <Calculator size={18} />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
              <span>Mortgage Statement Auto-Split</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-extrabold">
                Schedule E
              </span>
            </div>
            <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">
              Loan principal builds balance sheet equity and is <strong>0% tax deductible</strong>. Interest & escrow are <strong>100% deductible</strong>.
            </p>
          </div>
        </div>

        {/* Deductible % Badge */}
        <div className="text-right shrink-0">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800/60 shadow-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            <span>{pct.toFixed(0)}% Deductible</span>
          </span>
        </div>
      </div>

      {/* Summary Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs min-w-0">
        {/* Total Payment */}
        <div className="p-3 rounded-xl bg-white/80 dark:bg-slate-900/60 border border-[var(--border)] min-w-0">
          <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium truncate">Total Statement Payment</div>
          <div className="text-base sm:text-lg font-black text-slate-900 dark:text-slate-100 mt-0.5 tabular-nums truncate">
            ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Deductible Portion */}
        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 min-w-0">
          <div className="text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold flex items-center gap-1 min-w-0">
            <ShieldCheck size={13} className="shrink-0" />
            <span className="truncate">Schedule E Deductible</span>
          </div>
          <div className="text-base sm:text-lg font-black text-emerald-700 dark:text-emerald-400 mt-0.5 tabular-nums truncate">
            ${calcDeductible.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-emerald-600 dark:text-emerald-500/90 mt-0.5 truncate">
            Interest + Escrow taxes/insurance
          </div>
        </div>

        {/* Non-Deductible Principal */}
        <div className="p-3 rounded-xl bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 min-w-0">
          <div className="text-[11px] text-slate-600 dark:text-slate-400 font-semibold flex items-center gap-1 min-w-0">
            <Building size={13} className="shrink-0" />
            <span className="truncate">Non-Deductible Principal</span>
          </div>
          <div className="text-base sm:text-lg font-black text-slate-800 dark:text-slate-200 mt-0.5 tabular-nums truncate">
            ${nonDeductible.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">
            Builds equity (0% Schedule E)
          </div>
        </div>
      </div>

      {/* Breakdown Breakdown Inputs (editable or read-only) */}
      {!readOnly ? (
        <div className="p-3.5 rounded-xl bg-white/60 dark:bg-slate-900/40 border border-[var(--border)] space-y-3 min-w-0">
          <div className="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between gap-2 min-w-0">
            <span className="flex items-center gap-1.5 min-w-0 truncate">
              <Sparkles size={13} className="text-teal-500 shrink-0" />
              <span className="truncate">Extracted Item Breakdown (Adjust if needed):</span>
            </span>
            <span className="text-[10px] font-normal text-slate-500 dark:text-slate-400 shrink-0 hidden sm:inline">
              Live updates deductible amount & %
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div>
              <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1 truncate">
                Principal Amount (0%)
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold pointer-events-none select-none z-10">$</span>
                <input
                  type="number"
                  step="any"
                  className="lapen-input text-xs py-1.5 !pl-8 font-bold"
                  style={{ paddingLeft: "2rem" }}
                  value={principal || ""}
                  onChange={(e) => handlePrincipalChange(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1 truncate">
                Interest Amount (100%)
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold pointer-events-none select-none z-10">$</span>
                <input
                  type="number"
                  step="any"
                  className="lapen-input text-xs py-1.5 !pl-8 font-bold"
                  style={{ paddingLeft: "2rem" }}
                  value={interest || ""}
                  onChange={(e) => handleInterestChange(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1 truncate">
                Escrow Taxes & Ins (100%)
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold pointer-events-none select-none z-10">$</span>
                <input
                  type="number"
                  step="any"
                  className="lapen-input text-xs py-1.5 !pl-8 font-bold"
                  style={{ paddingLeft: "2rem" }}
                  value={escrow || ""}
                  onChange={(e) => handleEscrowChange(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-xl bg-white/60 dark:bg-slate-900/40 border border-[var(--border)] text-xs space-y-1.5 min-w-0">
          <div className="flex justify-between items-center gap-2 min-w-0 text-slate-600 dark:text-slate-400">
            <span className="truncate">Mortgage Interest (Deductible):</span>
            <span className="font-semibold text-slate-900 dark:text-slate-100 shrink-0 tabular-nums">${interest.toFixed(2)}</span>
          </div>
          {escrow > 0 && (
            <div className="flex justify-between items-center gap-2 min-w-0 text-slate-600 dark:text-slate-400">
              <span className="truncate" title="Escrow Property Taxes & Hazard Insurance (Deductible)">Escrow Property Taxes & Hazard Insurance (Deductible):</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100 shrink-0 tabular-nums">${escrow.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center gap-2 min-w-0 text-slate-600 dark:text-slate-400 border-t border-[var(--border)] pt-1.5">
            <span className="truncate">Principal Equity Reduction (Non-Deductible):</span>
            <span className="font-semibold text-slate-900 dark:text-slate-100 shrink-0 tabular-nums">${principal.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
